import { inject, Injectable } from '@angular/core'
import {
  ActivatedRoute,
  ActivationStart,
  NavigationEnd,
  NavigationExtras,
  NavigationStart,
  ParamMap,
  Params,
  Router,
} from '@angular/router'
import { _last, _numberEnumKeyOrUndefined } from '@naturalcycles/js-lib'
import {
  clickIdParameters,
  cookieService,
  GoalBreakpointOption,
  RegistrationFlow,
  WebSignupQueryParam,
} from '@naturalcycles/shared'
import { AnalyticsService } from '@src/app/core/services/analytics/analytics.service.js'
import { SeoService } from '@src/app/core/services/seo.service.js'
import { SessionService } from '@src/app/core/services/session.service.js'
import { getState, SignalStore } from '@src/app/core/store/signalStore.js'
import { isE2E } from '@src/app/core/util/e2e.util.js'
import { PageViewData } from '@src/app/shared/typings/analytics.js'
import { AnyPage, Page, ParentPage } from '@src/app/shared/typings/enum/pages.js'
import {
  defaultCbQueryParams,
  defaultQueryParams,
} from '@src/app/shared/typings/enum/query-params.js'
import { env } from '@src/environments/environment.js'
import { hasParentPage } from '../util/nav.util.js'
import { DiscountCodeService } from './discountCode.service.js'
import { QuizService } from './quiz/quiz.service.js'

@Injectable({ providedIn: 'root' })
export class NavService {
  private router = inject(Router)
  private route = inject(ActivatedRoute)
  private sessionService = inject(SessionService)
  private seo = inject(SeoService)
  private analyticsService = inject(AnalyticsService)
  private discountCodeService = inject(DiscountCodeService)
  private quizService = inject(QuizService)
  private store = inject(SignalStore)

  init(): void {
    this.router.events.subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.seo.setCanonical(location.href)

        const { path, lang } = this.parseUrl(e.url)
        const { queryParams } = this.route.snapshot
        const data: PageViewData = {
          lang,
          consentCookie: cookieService.getCookie('CookieConsent'),
          ...queryParams,
          // ownsOuraRing and ownsAppleWatch are used here for back compatibility reasons. Both parameters used to be included in queryParams
          ownsOuraRing:
            queryParams[WebSignupQueryParam.flow] === RegistrationFlow.OURA.toString()
              ? this.store.$hardware().ownsDevice?.toString()
              : undefined,
          ownsAppleWatch:
            queryParams[WebSignupQueryParam.flow] === RegistrationFlow.APPLE_WATCH.toString()
              ? this.store.$hardware().ownsDevice?.toString()
              : undefined,
        }

        for (const clickIdParameter of clickIdParameters) {
          if (queryParams[clickIdParameter]) {
            cookieService.setCookie(
              clickIdParameter,
              queryParams[clickIdParameter],
              30,
              '.naturalcycles.com',
            )
          }
        }

        const code = queryParams[WebSignupQueryParam.code]
        const referral =
          queryParams[WebSignupQueryParam.referral] || queryParams[WebSignupQueryParam.refId]
        if (code) {
          void this.discountCodeService.persistDiscountCode({ code, referral })
        }

        const ignoreConfirmation = queryParams[WebSignupQueryParam.ignoreConfirmation] === 'true'

        const isProductionE2E = isE2E() && env.prod

        /* We monitor the request for this event in production e2e, but in other e2e it's unnecessary bloat
         Additionally, ignoreConfirmation will immediately redirect to a different page, so we don't want to
         track that as a page view */
        const shouldTrackPageView = !ignoreConfirmation && (isProductionE2E || !isE2E())
        if (shouldTrackPageView) {
          void this.analyticsService.pageView(path, data)
        }
      }

      if (e instanceof NavigationStart) {
        /*
         * This router event runs before bootstrap is complete and possibly before the store is initialized.
         * For this reason, we return early if bootstrap is not complete,
         * but we take over the validation of the quiz signature on every other navigation.
         */
        if (!this.store.$bootstrapComplete()) return

        const so = this.quizService.getSignatureObject()
        this.quizService.validateQuizSignature(so)
      }

      if (e instanceof ActivationStart) {
        const { queryParams } = e.snapshot

        if (queryParams[WebSignupQueryParam.fg]) {
          const asNumber = parseInt(queryParams[WebSignupQueryParam.fg], 10)
          const fertilityGoalKey = _numberEnumKeyOrUndefined(GoalBreakpointOption, asNumber)
          if (fertilityGoalKey) {
            const fertilityGoal = GoalBreakpointOption[fertilityGoalKey]
            this.store.$qpFertilityGoal.set(fertilityGoal)
            this.store.persistToLocalStorage()
          }
        }
      }
    })
  }

  /**
   * a method for parsing url to path and language
   * e.g.) /en-US/signup/secret -> { path: '/signup/secret', lang: 'en-US' }
   */
  parseUrl(url: string): { path: string; lang: string } {
    // remove leading slash
    let path = url.slice(1)
    const urlParts = path.split('/')
    // remove lang
    const lang = urlParts.shift()!
    // reconstruct url without query params
    path = `/${urlParts.join('/').split('?')[0]}`
    return { path, lang }
  }

  async goToPage(
    relativePath: (AnyPage | string)[],
    extras: NavigationExtras & { customDeviceParams?: WebSignupQueryParam[] } = {},
  ): Promise<boolean> {
    const { lang } = getState()
    const path = [lang, ...relativePath]
    const isClearblue = hasParentPage(ParentPage.clearblue, `/${lang}/${relativePath.join('/')}`)
    const paramsToForward = extras.customDeviceParams || [
      ...(isClearblue ? defaultCbQueryParams : []),
      ...defaultQueryParams,
      ...this.getInsuranceCaptureParams(_last(relativePath)),
    ]
    const deviceParams: { [key: string]: string | null } = {}
    paramsToForward.forEach(param => {
      deviceParams[param] = this.getQueryParam(param)
    })

    extras.queryParams = {
      ...deviceParams,
      ...extras.queryParams,
    }
    return await this.router.navigate(path, extras)
  }

  async goToWebApp(): Promise<void> {
    const qs = new URLSearchParams(location.search)
    const { lang } = getState()
    qs.set('lang', lang)
    const sessionToken = await this.sessionService.generateToken()
    if (sessionToken) {
      qs.set('sessionToken', sessionToken)
    }

    const search = qs.toString()
    window.location.href = `${env.appUrl}?${search}${location.hash}`
  }

  getQueryParams(): Params {
    return this.route.snapshot.queryParams
  }

  getQueryParamMap(): ParamMap {
    return this.route.snapshot.queryParamMap
  }

  getQueryParam(param: WebSignupQueryParam): string | null {
    return this.getQueryParamMap().get(param)
  }

  getCurrentUrl(): string {
    return this.router.url
  }

  getCurrentPath(): string {
    return this.parseUrl(this.getCurrentUrl()).path
  }

  /**
   * Gets custom query params for insurance capture pages. We need some logic to handle
   * backwards navigation clearing the params properly.
   */
  private getInsuranceCaptureParams(page: string): WebSignupQueryParam[] {
    // They shouldn't have any of the params at this stage (or should have them reset)
    if ([Page.coveredByInsurance, Page.chooseProvider].includes(page as Page)) {
      return []
    }
    /*
     * insuranceProviderOption is always required from this point on.
     * Having the variant here as well would cause trouble if:
     * 1. You pick a provider that requires clarification
     * 2. You choose a variant
     * 3. You go back and pick a provider that doesn't require clarification
     *
     * In this case you would still have the variant param set, which is not good.
     */
    const params = [WebSignupQueryParam.ip]
    if (page === Page.chooseProvider) {
      return params
    }
    return [...params, WebSignupQueryParam.ipv]
  }
}
